import { ApiFilled, CheckCircleFilled, CloseCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { Card, Descriptions, DescriptionsProps, Flex, Popover, Typography } from "antd";
import React, { useMemo } from "react";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { formatNumber } from "src/utils/formatting";
import { convertFahrenheitToCelsius } from "src/utils/generalUtils";
import NoticeCardSection from "./NoticeCard/NoticeCardSection";
import PlenumTempInfoContent from "./HmiView/PlenumTempInfoContent";

const { Text } = Typography;

interface SystemControlsProps {
  binDto: PremierPlusBinDTO | null;
}

interface OnOffAmpsProps {
  isOn: boolean;
  amps: number | null | undefined;
}

const OnOffAmps: React.FC<OnOffAmpsProps> = ({ isOn, amps }) => {
  const icon = isOn ? (
    <>
      ON <CheckCircleFilled style={{ color: "green" }} />
    </>
  ) : (
    <>
      OFF <CloseCircleFilled style={{ color: "red" }} />
    </>
  );

  const ampsContent = useMemo(() => {
    if (amps == null) {
      return (
        <>
          Unknown <ApiFilled style={{ color: "gray" }} />
        </>
      );
    }

    return (
      <>
        {formatNumber(amps, { decimalPlaces: 1, suffix: " Amps", filler: "--" })}{" "}
        <ApiFilled style={{ color: isOn ? "green" : "red" }} />
      </>
    );
  }, [amps, isOn]);

  return (
    <div style={{ display: "flex", gap: "8px" }}>
      {icon}
      <Popover content="This represents the amperage of the entity">
        <span>{ampsContent}</span>
      </Popover>
    </div>
  );
};

const SystemControls: React.FC<SystemControlsProps> = ({ binDto }) => {
  const layout: "horizontal" = "horizontal";
  const itemsRest: DescriptionsProps["items"] = [];

  const useCelsius = !!binDto?.platformSettings?.useMetricSystem;
  const tempUnit = useCelsius ? "°C" : "°F";

  const toDisplayTemp = useMemo(() => {
    return (value?: number | null) => {
      if (value == null) return value;
      return useCelsius ? convertFahrenheitToCelsius(value) : value;
    };
  }, [useCelsius]);

  const burnerState = useMemo(() => {
    const fans = binDto?.fans ?? [];
    const highFireOn = fans.some((f) => !!f?.highFireOn);
    const lowFireOn = fans.some((f) => !!f?.lowFireOn);

    if (highFireOn) return "High Fire";
    if (lowFireOn) return "Low Fire";
    return "Off";
  }, [binDto]);

  const headspaceRh = useMemo(() => {
    const readings = (binDto?.gaMs ?? [])
      .map((g) => g?.relativeHumidity ?? null)
      .filter((r): r is number => r != null);

    if (readings.length === 0) return null;

    return readings.reduce((s, v) => s + v, 0) / readings.length;
  }, [binDto]);

  const headspaceTemp = useMemo(() => {
    const readings = (binDto?.gaMs ?? [])
      .map((g) => g?.temperatureF ?? null)
      .filter((r): r is number => r != null);

    if (readings.length === 0) return null;

    const avgF = readings.reduce((s, v) => s + v, 0) / readings.length;
    return toDisplayTemp(avgF);
  }, [binDto, toDisplayTemp]);

  const ambientTemp = useMemo(() => {
    return toDisplayTemp(binDto?.averageAmbientRhtSensor_TemperatureF);
  }, [binDto, toDisplayTemp]);

  const plenumTemp = useMemo(() => {
    return toDisplayTemp(binDto?.averagePlenumRhtSensor_TemperatureF);
  }, [binDto, toDisplayTemp]);

  const headspaceEmc = useMemo(() => {
    const readings = (binDto?.gaMs ?? [])
      .map((g) => g?.emc ?? null)
      .filter((r): r is number => r != null);

    if (readings.length === 0) return null;

    return readings.reduce((s, v) => s + v, 0) / readings.length;
  }, [binDto]);

  const totalVolumeBushels = useMemo(() => {
    return binDto?.grainHeightState_TotalVolumeBushels ?? null;
  }, [binDto]);

  const fillPercent = useMemo(() => {
    return binDto?.grainHeightState_PercentFull ?? null;
  }, [binDto]);

  const displayGrainHeightCenter = useMemo(() => {
    return formatNumber(binDto?.grainHeightState_HeightAtPeak ?? null, {
      decimalPlaces: 1,
      showSuffixIfNoValue: true,
      suffix: `'c`,
    });
  }, [binDto]);

  const displayGrainHeightPerimeter = useMemo(() => {
    return formatNumber(binDto?.grainHeightState_HeightAtEave ?? null, {
      decimalPlaces: 1,
      showSuffixIfNoValue: true,
      suffix: `'p`,
    });
  }, [binDto]);

  const displayEstimatedBushels = useMemo(() => {
    return formatNumber(totalVolumeBushels, {
      decimalPlaces: 0,
      showSuffixIfNoValue: true,
      suffix: `bu`,
    });
  }, [totalVolumeBushels]);

  const displayFillLevelPercent = useMemo(() => {
    return formatNumber(fillPercent, {
      decimalPlaces: 1,
      showSuffixIfNoValue: true,
      suffix: `%`,
    });
  }, [fillPercent]);

  itemsRest.push({
    label: "Machine Control",
    children: (
      <OnOffAmps
        isOn={!!binDto?.isMachineOn}
        amps={binDto?.machineCurrent_mA != null ? binDto.machineCurrent_mA / 1000 : null}
      />
    ),
  });

  (binDto?.augers ?? []).forEach((a, idx) => {
    itemsRest.push({
      label: `Auger ${idx + 1}`,
      children: (
        <OnOffAmps
          isOn={!!a?.isOn}
          amps={a?.current_mA != null ? a.current_mA / 1000 : null}
        />
      ),
    });
  });

  (binDto?.fans ?? []).forEach((f, idx) => {
    const milliAmps = f?.fan?.current?.current_mA ?? null;
    const amps = milliAmps != null ? milliAmps / 1000.0 : null;

    itemsRest.push({
      label: `Fan ${idx + 1}`,
      children: <OnOffAmps isOn={!!f?.isOn} amps={amps} />,
    });
  });

  itemsRest.push({
    label: "Burner State",
    children: <Text>{burnerState}</Text>,
  });

  (binDto?.fans ?? []).forEach((f, idx) => {
    itemsRest.push({
      label: `Burner ${idx + 1}`,
      children: <OnOffAmps isOn={!!f?.isHeaterOn} amps={f?.heaterAmps ?? null} />,
    });
  });

  itemsRest.push(
    {
      label: "Headspace Air Conditions",
      children: (
        <Text>
          {formatNumber(headspaceTemp, { decimalPlaces: 1, filler: "--" })}{tempUnit} |{" "}
          {formatNumber(headspaceRh, { decimalPlaces: 1, filler: "--" })}% RH |{" "}
          {formatNumber(headspaceEmc, { decimalPlaces: 1, filler: "--" })}% EMC
        </Text>
      ),
    },
    {
      label: "Ambient Air Conditions",
      children: (
        <Text>
          {formatNumber(ambientTemp, { decimalPlaces: 1, filler: "--" })}{tempUnit} |{" "}
          {formatNumber(binDto?.averageAmbientRhtSensor_RhPercent ?? null, { decimalPlaces: 1, filler: "--" })}% RH |{" "}
          {formatNumber(binDto?.averageAmbientRhtSensor_McPercent ?? null, { decimalPlaces: 1, filler: "--" })}% EMC
        </Text>
      ),
    },
    {
      label: (
        <Flex align="center" gap={6}>
          Plenum Air Conditions
          <Popover
            trigger="click"
            title="Individual Plenum Temp Info"
            content={<PlenumTempInfoContent binDto={binDto} />}
          >
            <InfoCircleOutlined style={{ cursor: "pointer" }} />
          </Popover>
        </Flex>
      ),
      children: (
        <Text>
          {formatNumber(plenumTemp, { decimalPlaces: 1, filler: "--" })}{tempUnit} |{" "}
          {formatNumber(binDto?.averagePlenumRhtSensor_RhPercent ?? null, { decimalPlaces: 1, filler: "--" })}% RH |{" "}
          {formatNumber(binDto?.averagePlenumRhtSensor_McPercent ?? null, { decimalPlaces: 1, filler: "--" })}% EMC
        </Text>
      ),
    },
    {
      label: "Fill Level",
      children: (
        <div style={{ display: "flex", gap: "12px" }}>
          <Text>{displayGrainHeightCenter}</Text>|
          <Text>{displayGrainHeightPerimeter}</Text>|
          <Text>{displayEstimatedBushels}</Text>|
          <Text>{displayFillLevelPercent}</Text>
        </div>
      ),
    }
  );

  return (
    <Card
      style={{ width: "100%" }}
      title={
        <Flex gap="small">
          <span>System Controls</span>
        </Flex>
      }
    >
      <NoticeCardSection binDto={binDto} />
      <br />
      <Descriptions bordered layout={layout} items={itemsRest} size="small" column={1} />
    </Card>
  );
};

export default SystemControls;